import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Scale, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LawyerApply = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    bar_id: '',
    firm_name: '',
    bio: '',
    specialties: [],
    years_experience: 0,
    insurance_confirmed: false
  });

  const specialtyOptions = [
    'Leieforhold',
    'Arbeidsrett',
    'Familierett',
    'Forbrukerrett',
    'Erstatningsrett',
    'Kontraktsrett',
    'Selskapsrett',
    'Strafferett'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/lawyer/apply`, formData);
      setSubmitted(true);
      toast.success('Søknad sendt!');
    } catch (error) {
      toast.error('Kunne ikke sende søknad');
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
        <div className="max-w-2xl text-center">
          <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Søknad mottatt! 🎉
          </h1>
          <p className="text-xl text-slate-600 mb-8">
            Vi gjennomgår din søknad og kommer tilbake innen 48 timer.
            <br />
            Sjekk e-post for bekreftelse.
          </p>
          <button
            onClick={() => navigate('/')}
            className="text-slate-600 hover:text-slate-900 underline"
          >
            Tilbake til forsiden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <button type="button" onClick={() => navigate('/')} className="w-9 h-9 flex items-center justify-center hover:bg-slate-100 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-slate-900" />
            <span className="font-bold text-slate-900">Advokat for alle</span>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Bli med som advokat
            </h1>
            <p className="text-xl text-slate-600">
              Få flere klienter og jobb fleksibelt
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-3xl p-10 shadow-lg space-y-6">
            
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2">Fullt navn</label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                className="h-12 rounded-xl"
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">E-post</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="h-12 rounded-xl"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">Telefon</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="h-12 rounded-xl"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">MNA-nummer</label>
                <Input
                  value={formData.bar_id}
                  onChange={(e) => setFormData({...formData, bar_id: e.target.value})}
                  placeholder="12345"
                  className="h-12 rounded-xl"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">Årsverk erfaring</label>
                <Input
                  type="number"
                  value={formData.years_experience}
                  onChange={(e) => setFormData({...formData, years_experience: parseInt(e.target.value)})}
                  className="h-12 rounded-xl"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2">Firma/Praksis</label>
              <Input
                value={formData.firm_name}
                onChange={(e) => setFormData({...formData, firm_name: e.target.value})}
                placeholder="Ditt advokatfirma AS"
                className="h-12 rounded-xl"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2">Kompetanseområder (velg minst 2)</label>
              <div className="grid grid-cols-2 gap-3 p-4 bg-slate-50 rounded-xl">
                {specialtyOptions.map((specialty) => (
                  <div key={specialty} className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.specialties.includes(specialty)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({...formData, specialties: [...formData.specialties, specialty]});
                        } else {
                          setFormData({...formData, specialties: formData.specialties.filter(s => s !== specialty)});
                        }
                      }}
                    />
                    <label className="text-sm text-slate-700">{specialty}</label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2">Kort bio (synlig for kunder)</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                className="w-full h-32 p-4 border border-slate-300 rounded-xl resize-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Fortell litt om din erfaring og kompetanse..."
                required
              />
            </div>

            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <Checkbox
                checked={formData.insurance_confirmed}
                onCheckedChange={(checked) => setFormData({...formData, insurance_confirmed: checked === true})}
                required
              />
              <label className="text-sm text-amber-900 leading-relaxed">
                Jeg bekrefter at jeg har gyldig yrkesansvarsforsikring og er autorisert MNA-advokat
              </label>
            </div>

            <Button
              type="submit"
              disabled={formData.specialties.length < 2 || !formData.insurance_confirmed}
              className="w-full bg-slate-900 hover:bg-slate-800 h-14 text-lg font-bold rounded-xl"
            >
              Send søknad
            </Button>

            <p className="text-xs text-slate-500 text-center">
              Vi gjennomgår alle søknader manuelt og kommer tilbake innen 48 timer
            </p>
          </form>

        </div>
      </main>
    </div>
  );
};

export default LawyerApply;
